/**
 * /accounting/reports/balance-sheet — A = L + E at period end.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import {
  computeBalanceSheet, listAvailablePeriods, formatPeriodLabel, type PeriodKey,
} from "@/data/accounting/reports/financialReports";

const fmt = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;

export default function BalanceSheetPage() {
  useSeedGeneralLedger();
  const { accounts, journalEntries, periodLocks } = useGeneralLedger();
  const periods = useMemo<PeriodKey[]>(
    () => ["all", ...listAvailablePeriods(journalEntries)],
    [journalEntries],
  );
  const [period, setPeriod] = useState<PeriodKey>(periods[1] ?? "all");
  const closedPeriods = useMemo(
    () => new Set(periodLocks.filter(p => p.status === "closed").map(p => p.period)),
    [periodLocks],
  );
  const bs = useMemo(
    () => computeBalanceSheet(accounts, journalEntries, period, closedPeriods),
    [accounts, journalEntries, period, closedPeriods],
  );

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6">
        <Link to="/accounting/reports" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to Reports
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 data-tour-id="balance-sheet-title" className="text-2xl font-semibold tracking-tight">Balance Sheet</h1>
            <p className="text-sm text-muted-foreground mt-1">As of {formatPeriodLabel(period)}</p>
          </div>
          <select
            className="text-sm border border-border rounded-md px-2 py-1.5 bg-card"
            value={period}
            onChange={e => setPeriod(e.target.value)}
          >
            {periods.map(p => (
              <option key={p} value={p}>{formatPeriodLabel(p)}</option>
            ))}
          </select>
        </div>
      </div>

      {!bs.inBalance && (
        <div className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 text-destructive p-3 text-sm flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Out of balance</div>
            <div className="text-xs">
              Assets {fmt(bs.assets.total)} ≠ Liabilities + Equity {fmt(bs.liabilitiesAndEquity)}.
              Inspect recent journals.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Side title="Assets" total={bs.assets.total} rows={bs.assets.rows} />
        <div className="space-y-4">
          <Side title="Liabilities" total={bs.liabilities.total} rows={bs.liabilities.rows} />
          <CockpitSection>
            <div className="rounded-md border border-border bg-card">
              <div className="px-3 py-2 flex items-baseline justify-between border-b border-border">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Equity</div>
                <div className="text-sm font-semibold tabular-nums">{fmt(bs.equity.total)}</div>
              </div>
              <div className="divide-y divide-border">
                {bs.equity.rows.map(r => (
                  <Link key={r.accountId} to={`/accounting/gl/detail?account=${r.accountId}`} className="px-3 py-1.5 flex justify-between text-sm hover:bg-accent/40">
                    <span className="text-muted-foreground"><span className="tabular-nums mr-2">{r.number}</span>{r.name}</span>
                    <span className="tabular-nums">{fmt(r.balance)}</span>
                  </Link>
                ))}
                <div className="px-3 py-1.5 flex justify-between text-sm">
                  <span className="text-muted-foreground">Net income (current)</span>
                  <span className={`tabular-nums ${bs.equity.netIncome < 0 ? "text-destructive" : ""}`}>{fmt(bs.equity.netIncome)}</span>
                </div>
              </div>
            </div>
          </CockpitSection>
          <div className="rounded-md border border-border bg-card p-3 flex justify-between text-sm font-semibold">
            <span>Liabilities + Equity</span>
            <span className="tabular-nums">{fmt(bs.liabilitiesAndEquity)}</span>
          </div>
        </div>
      </div>
    </CockpitPage>
  );
}

function Side({ title, total, rows }: {
  title: string;
  total: number;
  rows: { accountId: string; number: string; name: string; balance: number }[];
}) {
  return (
    <CockpitSection>
      <div className="rounded-md border border-border bg-card">
        <div className="px-3 py-2 flex items-baseline justify-between border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{title}</div>
          <div className="text-sm font-semibold tabular-nums">{fmt(total)}</div>
        </div>
        <div className="divide-y divide-border">
          {rows.length === 0 && (
            <div className="px-3 py-3 text-sm text-muted-foreground">No balances.</div>
          )}
          {rows.map(r => (
            <Link key={r.accountId} to={`/accounting/gl/detail?account=${r.accountId}`} className="px-3 py-1.5 flex justify-between text-sm hover:bg-accent/40">
              <span className="text-muted-foreground"><span className="tabular-nums mr-2">{r.number}</span>{r.name}</span>
              <span className="tabular-nums">{fmt(r.balance)}</span>
            </Link>
          ))}
        </div>
      </div>
    </CockpitSection>
  );
}
